import { hasTableCellNavigation, isInteractiveCellTarget, runTableCellNavigation, TableCellNavigationDirective, tableCellNavigationTarget } from '../../../chart-components/unified-table-matrix-chart/table-cell-navigation.directive';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import type { Subscription } from 'rxjs';

import { ConfigService } from '../../../shared-services/config.service';
import { rebaseSelfServiceApiUrl } from '../../shared/self-service-api.utils';
import {
  cellAccent,
  cellBadgeLookStyle,
  cellBarPercent,
  cellCurrency,
  cellDisplayNeedsRange,
  cellDropdownOptions,
  cellHeatColor,
  cellLookStyle,
  cellTrend,
  decorateCellText,
  resolveCellLook,
  substituteCellTokens,
  type CellValueRange,
  type ResolvedCellLook,
  type TableCellConfig,
} from '../../../chart-components/unified-table-matrix-chart/table-cell-config';
import { nestedCellKind, nestedColumnCellConfig, type NestedCellKind } from './nested-cell-config.utils';
import type { TableCellButton } from '../../../chart-components/unified-table-matrix-chart/table-cell-config';

import type {
  NestedTableLevelQuery,
  NestedTableRowSave,
  NestedTableRowSaveEvent,
  NestedOption,
  NestedTableActionEvent,
  NestedTableCellButtonEvent,
  NestedTableCellChange,
  NestedTableColumn,
  NestedTableData,
  NestedTableLevel,
  NestedTableRow,
  NestedTableTheme,
  NestedSortKey,
} from './nested-table.types';
import { NESTED_THEME_KEYS } from './nested-table.types';
import { sortNestedRows } from './nested-table-sort.utils';
import { nestedQueryRows, normalizeNestedQueryRows, resolveNestedLevelQuery } from './nested-table-query.utils';
import { resolveLevel, resolveLevelColumns, rowsByLevel } from './nested-table.utils';
import { levelCellSize, levelHeaderSize, textSizePx } from '../../../chart-components/unified-table-matrix-chart/table-text-size';

const DEFAULT_THEME: NestedTableTheme = {
  headerBackground: '#1f2a44',
  headerColor: '#ffffff',
  rowBackground: '#ffffff',
  expandedRowBackground: '#e9eef7',
  panelBackground: '#f1f4f9',
  borderColor: '#e4e7ec',
  textColor: '#101828',
  accentColor: '#4a8fe7',
  fontFamily: "Inter, Roboto, 'Segoe UI', sans-serif",
  fontSize: 13,
  radius: 8,
};

/**
 * Multi-level nested table. Each expandable row opens the next level as its
 * own titled table with its own columns and actions — see nested-table.types.
 *
 * Edits stay in a local deep copy (the host's config is never mutated) and are
 * reported through `cellChange`.
 */
@Component({
  selector: 'app-nested-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TableCellNavigationDirective, MatIconModule],
  templateUrl: './nested-table.component.html',
  styleUrl: './nested-table.component.scss',
})
export class NestedTableComponent implements AfterViewChecked {
  @Output() readonly cellChange = new EventEmitter<NestedTableCellChange>();
  @Output() readonly action = new EventEmitter<NestedTableActionEvent>();
  /** A button of a 'buttons' cell was clicked - the host runs its feature for that row. */
  @Output() readonly cellButton = new EventEmitter<NestedTableCellButtonEvent>();
  /** A row's Save (level `rowSave`: an update with business keys) was clicked. */
  @Output() readonly rowSave = new EventEmitter<NestedTableRowSaveEvent>();

  /** Fields edited per row key since its last save. */
  private readonly dirtyRows = new Map<string, Set<string>>();
  /** Per row key: a save in flight, just done, or failed. */
  private readonly saveStates = new Map<string, 'saving' | 'saved' | 'error'>();

  /** The rows above the one at `path` (row indices from the top level down). */
  /**
   * The row a navigation reads its filters from: the clicked row plus the rows
   * it sits under, so a level-2 spec link can send the level-1 customer too.
   * The row's own values win over a parent's of the same name.
   */
  navigationRow(key: string, row: NestedTableRow): NestedTableRow {
    if (!key || !key.includes('.')) return row;
    const ancestors = this.ancestorsAt(key.split('.').map(Number));
    return ancestors.length ? Object.assign({}, ...ancestors, row) : row;
  }

  private ancestorsAt(path: number[]): NestedTableRow[] {
    const out: NestedTableRow[] = [];
    let list = this.rows;
    for (let depth = 0; depth < path.length - 1; depth++) {
      const row = list[path[depth]];
      if (!row) break;
      out.push(row);
      list = this.children(row, depth);
    }
    return out;
  }

  rowSaveOf(levelIndex: number): NestedTableRowSave | null {
    const save = this.level(levelIndex)?.rowSave;
    return save?.url ? save : null;
  }

  /** Full-width rows (expansion, empty state) span every column. */
  rowSpan(levelIndex: number): number {
    return this.columns(levelIndex).length;
  }

  /** Per table (level + the row it opened under): a save in flight, done, or partly failed. */
  private readonly tableSaveStates = new Map<string, 'saving' | 'saved' | 'error'>();

  private tableKey(levelIndex: number, parentKey: string): string {
    return `${levelIndex}:${parentKey}`;
  }

  tableSaveState(levelIndex: number, parentKey: string): 'saving' | 'saved' | 'error' | null {
    return this.tableSaveStates.get(this.tableKey(levelIndex, parentKey)) ?? null;
  }

  /** Keys of this table's rows edited since their last save. */
  pendingKeys(parentKey: string, rows: NestedTableRow[]): string[] {
    return rows.map((_, index) => this.rowKey(parentKey, index)).filter((key) => !!this.dirtyRows.get(key)?.size);
  }

  /** Save shows once a row of this table is edited and every edited row has its mandatory columns. */
  canSaveTable(levelIndex: number, parentKey: string, rows: NestedTableRow[]): boolean {
    const pending = this.pendingKeys(parentKey, rows);
    return !!this.rowSaveOf(levelIndex) && pending.length > 0
      && pending.every((key) => this.canSave(key, rows[Number(key.split('.').pop())], levelIndex));
  }

  /** One update per edited row; the table's note sums them up when all have answered. */
  saveTable(levelIndex: number, parentKey: string, rows: NestedTableRow[]): void {
    const pending = this.pendingKeys(parentKey, rows);
    if (!pending.length || !this.canSaveTable(levelIndex, parentKey, rows)) return;
    const tableKey = this.tableKey(levelIndex, parentKey);
    this.tableSaveStates.set(tableKey, 'saving');
    let left = pending.length;
    let failed = false;
    pending.forEach((key) => {
      this.saveRow(levelIndex, key, rows[Number(key.split('.').pop())], (ok) => {
        failed = failed || !ok;
        left -= 1;
        if (left === 0) {
          this.tableSaveStates.set(tableKey, failed ? 'error' : 'saved');
          this.changeDetector.markForCheck();
        }
      });
    });
  }

  saveState(key: string): 'saving' | 'saved' | 'error' | null {
    return this.saveStates.get(key) ?? null;
  }

  /** Save shows once the row was edited AND every mandatory column has a value. */
  canSave(key: string, row: NestedTableRow, levelIndex: number): boolean {
    if (!this.rowSaveOf(levelIndex) || !this.dirtyRows.get(key)?.size) return false;
    // A paired mandatory column needs BOTH of its keys filled; a value outside
    // its min / max range (red) cannot be saved either.
    return this.columns(levelIndex).every((column) =>
      (!column.required || this.cellFields(column).every((field) => !isBlank(row[field])))
      && !this.isOutOfRange(column, row));
  }

  saveRow(levelIndex: number, key: string, row: NestedTableRow, after?: (ok: boolean) => void): void {
    const save = this.rowSaveOf(levelIndex);
    if (!save || this.saveStates.get(key) === 'saving') return;
    const editedFields = [...(this.dirtyRows.get(key) ?? [])];
    this.saveStates.set(key, 'saving');
    this.rowSave.emit({
      levelIndex,
      path: key.split('.').map(Number),
      row,
      save,
      editedFields,
      ancestors: this.ancestorsAt(key.split('.').map(Number)),
      done: (ok) => {
        if (ok) this.dirtyRows.delete(key);
        this.saveStates.set(key, ok ? 'saved' : 'error');
        after?.(ok);
        this.changeDetector.markForCheck();
      },
    });
  }

  /** A data cell was clicked — with Format "Click a value to filter" on, it filters the dashboard. */
  @Output() readonly valueClick = new EventEmitter<{ levelIndex: number; field: string; value: unknown }>();

  onValueClick(levelIndex: number, column: NestedTableColumn, row: NestedTableRow, event: Event, key = ''): void {
    // Column-level navigation (Cell formatting → On click): the cell value
    // opens the configured published dashboard filtered on this row.
    if (this.hasNavigation(column)) {
      if (isInteractiveCellTarget(event)) return;
      event.stopPropagation();
      runTableCellNavigation(this.router, this.cellConfig(column), this.navigationRow(key, row), column.field, row[column.field], event, this.navDefaultKey(levelIndex, column));
      return;
    }
    if (!this.flag('clickToFilter')) return;
    const kind = this.cellKind(column);
    if (kind === 'input' || kind === 'dropdown') return;
    if ((event.target as HTMLElement | null)?.closest('button, input, select, a')) return;
    this.valueClick.emit({ levelIndex, field: column.field, value: row[column.field] });
  }

  /** Column navigates on click (link cells carry their own anchor). */
  hasNavigation(column: NestedTableColumn): boolean {
    return hasTableCellNavigation(this.cellConfig(column)) && this.cellKind(column) !== 'link';
  }
  /** Filter field when the column's navigation names none: a NUMBER column
   *  sends this level's first data column (the row's key), a text column itself. */
  navDefaultKey(levelIndex: number, column: NestedTableColumn): string {
    const type = column.type ?? 'text';
    if (type !== 'number' && type !== 'currency' && type !== 'percent') return '';
    const first = this.columns(levelIndex).find((c) => c.type !== 'index' && c.field);
    return first?.field || '';
  }
  navTitle(column: NestedTableColumn): string | null {
    if (!this.hasNavigation(column)) return null;
    const target = tableCellNavigationTarget(this.cellConfig(column));
    return target ? `Click to open ${target}` : 'Click to navigate';
  }

  config: NestedTableData = { levels: [] };
  rows: NestedTableRow[] = [];
  theme: NestedTableTheme = DEFAULT_THEME;
  private expanded = new Set<string>();
  private rowSourceSnapshot?: string;
  private readonly visibleColumnsCache = new Map<number, NestedTableColumn[]>();

  private readonly configService = inject(ConfigService);
  private readonly router = inject(Router, { optional: true });
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject(ElementRef<HTMLElement>);

  // ── Sticky headers on every level ──────────────────────────────────────────
  // Every level's header is CSS sticky in the ROOT scroll area (smooth: the
  // browser moves it, no script on scroll), just below the headers of the
  // levels above - so its offset is their heights added up. Heights are
  // measured after each render (they follow text size / wrapping); the view
  // re-renders only when one changed.
  private headerHeights: number[] = [];

  /** `top` for a level's sticky header: the header heights of the levels above. */
  stickyTop(levelIndex: number): string {
    let top = 0;
    for (let i = 0; i < levelIndex; i++) top += this.headerHeights[i] ?? 0;
    return `${top}px`;
  }

  ngAfterViewChecked(): void {
    this.fitOuterTables();
    const next: number[] = [];
    (this.host.nativeElement as HTMLElement).querySelectorAll<HTMLElement>('.nt-table-wrap[data-nt-level]').forEach((wrap) => {
      const level = Number(wrap.dataset['ntLevel']);
      if (next[level] !== undefined) return;
      next[level] = wrap.querySelector<HTMLElement>(':scope > .nt-table > thead')?.offsetHeight ?? 0;
    });
    if (next.length !== this.headerHeights.length || next.some((h, i) => h !== this.headerHeights[i])) {
      this.headerHeights = next;
      // After this check: changing a binding inside it would throw in dev mode.
      queueMicrotask(() => this.changeDetector.markForCheck());
    }
  }

  /**
   * A level's table never ends short of what it holds: an expanded inner table
   * wider than its row (fixed "Header width" layouts do not grow on their own)
   * used to stick out past the row, the panel and the outer table. Deepest
   * tables first, each table gets a min-width of what its content really
   * needs, so every outer level widens to match and the root area scrolls
   * sideways as one. Plain DOM work after a render; no change detection.
   */
  private fitOuterTables(): void {
    const host = this.host.nativeElement as HTMLElement;
    const tables = [...host.querySelectorAll<HTMLElement>('.nt-table-wrap[data-nt-level] > .nt-table')]
      .sort((a, b) => depthOf(b) - depthOf(a));
    // Reset first, so a collapsed (narrower) breakdown lets its tables shrink back.
    tables.forEach((table) => { table.style.minWidth = ''; });
    tables.forEach((table) => {
      const needed = table.scrollWidth;
      if (needed > table.clientWidth + 1) table.style.minWidth = `${needed}px`;
    });

    function depthOf(table: HTMLElement): number {
      return Number(table.parentElement?.dataset['ntLevel'] ?? 0);
    }
  }

  private rowsRequest?: Subscription;
  private readonly childRequests = new Map<string, Subscription>();
  private readonly loadedChildren = new Set<string>();
  private readonly childStates = new Map<string, 'loading' | 'error'>();
  loading = false;
  loadError = '';

  /**
   * A "Save table changes" button asked for a save: each new number saves every
   * edited row, with the row Save the table already has. The outcome goes out
   * on `saveAllDone`.
   */
  @Input() set saveRequest(request: number | null | undefined) {
    if (!request || request === this.handledSaveRequest) return;
    this.handledSaveRequest = request;
    this.saveAllEdited();
  }
  private handledSaveRequest: number | null = null;

  @Output() readonly saveAllDone = new EventEmitter<{ saved: number; failed: number; blocked: number }>();

  /** Every edited row, at every level, through `saveRow`; rows missing a mandatory value are counted, not sent. */
  saveAllEdited(): void {
    const result = { saved: 0, failed: 0, blocked: 0 };
    const ready: Array<{ key: string; levelIndex: number; row: NestedTableRow }> = [];
    for (const [key, fields] of this.dirtyRows) {
      if (!fields.size || this.saveStates.get(key) === 'saving') continue;
      const path = key.split('.').map(Number);
      const row = this.rowAtPath(path);
      const levelIndex = path.length - 1;
      if (row && this.canSave(key, row, levelIndex)) ready.push({ key, levelIndex, row });
      else result.blocked += 1;
    }
    let left = ready.length;
    if (!left) {
      this.saveAllDone.emit(result);
      return;
    }
    ready.forEach(({ key, levelIndex, row }) => this.saveRow(levelIndex, key, row, (ok) => {
      if (ok) result.saved += 1; else result.failed += 1;
      left -= 1;
      if (left === 0) this.saveAllDone.emit(result);
    }));
  }

  /** The row at `path` (row indices from the top level down), or null. */
  private rowAtPath(path: number[]): NestedTableRow | null {
    let list = this.rows;
    for (let depth = 0; depth < path.length; depth++) {
      const row = list[path[depth]];
      if (!row) return null;
      if (depth === path.length - 1) return row;
      list = this.children(row, depth);
    }
    return null;
  }

  @Input() set data(value: NestedTableData | null | undefined) {
    this.config = value && Array.isArray(value.levels) ? value : { levels: [] };
    this.theme = { ...DEFAULT_THEME, ...(this.config.theme ?? {}), ...flatTheme(this.config) };
    this.visibleColumnsCache.clear();
    this.levelCache.clear();
    this.rowsRequest?.unsubscribe();
    this.childRequests.forEach((request) => request.unsubscribe());
    this.childRequests.clear();
    this.loadedChildren.clear();
    this.childStates.clear();
    this.loadError = '';

    const inline = this.config.staticData ?? this.config.rows;
    if (Array.isArray(inline) || !this.config.apiUrl) {
      this.setRows(Array.isArray(inline) ? inline : []);
      return;
    }
    this.loading = true;
    this.rowsRequest = this.configService
      .getConfig(this.config.apiUrl, this.config, {}, this.config.method)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: unknown) => this.setRows(extractRows(response, this.config.rowsKey)),
        error: () => {
          this.loadError = 'Could not load table data.';
          this.setRows([]);
        },
      });
  }

  private setRows(rows: NestedTableRow[]): void {
    // Formatting sends a new config (and often cloned rows). Row keys use
    // indices, so retain the user's expansion only for the same row hierarchy.
    const snapshot = JSON.stringify({
      widgetId: this.config.widgetId,
      apiUrl: this.config.apiUrl,
      rows,
      childrenFields: this.config.levels.map((_, index) => this.level(index)?.childrenField || 'children'),
    });
    const preserveExpansion = snapshot === this.rowSourceSnapshot;
    this.rowSourceSnapshot = snapshot;
    this.rows = cloneRows(rows);
    this.levelRows = rowsByLevel(this.config, this.rows);
    this.visibleColumnsCache.clear();
    this.rangeCache.clear();
    if (!preserveExpansion) this.expanded = this.initialExpansion(this.rows, 0, '');
    this.loading = false;
    this.changeDetector.markForCheck();
  }

  /** The column holding a level's expand arrow: the ticked non-index one, else the first non-index column. */
  expanderField(levelIndex: number): string | null {
    const columns = this.columns(levelIndex).filter((column) => column.type !== 'index');
    return (columns.find((column) => column.expander) ?? columns[0])?.field ?? null;
  }

  /** A Format toggle carried flat on the data (e.g. hideRowLines). */
  flag(key: string): boolean {
    return (this.config as unknown as Record<string, unknown>)[key] === true;
  }

  /** A default-on toggle switched off (e.g. "Column lines": showColumnLines). */
  lineFlagOff(key: string): boolean {
    return (this.config as unknown as Record<string, unknown>)[key] === false;
  }

  get themeVars(): Record<string, string> {
    const t = this.theme;
    const config = this.config as unknown as Record<string, unknown>;
    const headerHeight = textSizePx(config['tableHeaderHeight']);
    const px = (key: string) => {
      const size = textSizePx(config[key]);
      return size === null ? {} : { [`--nt-${key.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())}`]: `${size}px` };
    };
    const text = (key: string) => {
      const value = config[key];
      return typeof value === 'string' && value.trim() ? { [`--nt-${key.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())}`]: value } : {};
    };
    return {
      // Format "Title" group + "Row height" (vars read by the stylesheet).
      ...px('rowHeight'),
      ...px('titleFontSize'),
      ...text('titleColor'),
      ...text('titleFontFamily'),
      ...px('subtitleFontSize'),
      ...text('subtitleColor'),
      ...text('subtitleFontFamily'),
      '--nt-header-bg': t.headerBackground,
      '--nt-header-color': t.headerColor,
      '--nt-row-bg': t.rowBackground,
      '--nt-expanded-bg': t.expandedRowBackground,
      '--nt-panel-bg': t.panelBackground,
      '--nt-border': t.borderColor,
      '--nt-text': t.textColor,
      '--nt-accent': t.accentColor,
      '--nt-font': t.fontFamily,
      '--nt-font-size': `${t.fontSize}px`,
      // Format "Header height" — one value for every level's header row.
      ...(headerHeight !== null ? { '--nt-th-height': `${headerHeight}px` } : {}),
      '--nt-radius': `${t.radius}px`,
    };
  }

  private levelRows: NestedTableRow[][] = [];
  private readonly levelCache = new Map<number, NestedTableLevel | null>();

  // ── Format "Text size" (see table-text-size.ts) ────────────────────────────
  // Set on EVERY level's table, as 'initial' when unset: custom properties
  // inherit, and a child level's table sits inside its parent's cell — without
  // the reset, level 2 would silently take level 1's size.
  headerSizeVar(levelIndex: number): string {
    const size = levelHeaderSize(this.config as unknown as Record<string, unknown>, levelIndex + 1);
    return size === null ? 'initial' : `${size}px`;
  }

  /**
   * Format "Level n font family". Always set: font-family inherits, and a
   * child level's table sits inside its parent's cell — unset, it falls back to
   * the table's font, never to the parent level's.
   */
  levelFontFamily(levelIndex: number): string {
    const family = (this.config as unknown as Record<string, unknown>)[`level${levelIndex + 1}FontFamily`];
    return typeof family === 'string' && family.trim() ? family : 'var(--nt-font)';
  }

  /** Format "Level n → Header / Row height"; 'initial' (= the all-levels value) when unset. */
  levelPxVar(levelIndex: number, key: 'HeaderHeight' | 'RowHeight'): string {
    const size = textSizePx((this.config as unknown as Record<string, unknown>)[`level${levelIndex + 1}${key}`]);
    return size === null ? 'initial' : `${size}px`;
  }

  /** Format "Level n → Level title size / colour / font family" for that level's heading. */
  levelTitleStyle(levelIndex: number): Record<string, string> {
    const config = this.config as unknown as Record<string, unknown>;
    const n = levelIndex + 1;
    const size = textSizePx(config[`level${n}TitleFontSize`]);
    const color = config[`level${n}TitleColor`];
    const family = config[`level${n}TitleFontFamily`];
    return {
      ...(size !== null ? { 'font-size': `${size}px` } : {}),
      ...(typeof color === 'string' && color.trim() ? { color } : {}),
      ...(typeof family === 'string' && family.trim() ? { 'font-family': family } : {}),
    };
  }

  /** Format "Level n → Header background / Header text colour"; 'initial' (= the table's) when unset. */
  levelColorVar(levelIndex: number, key: 'HeaderBackground' | 'HeaderColor'): string {
    const color = (this.config as unknown as Record<string, unknown>)[`level${levelIndex + 1}${key}`];
    return typeof color === 'string' && color.trim() ? color : 'initial';
  }

  cellSizeVar(levelIndex: number): string {
    const size = levelCellSize(this.config as unknown as Record<string, unknown>, levelIndex + 1);
    return size === null ? 'initial' : `${size}px`;
  }

  level(index: number): NestedTableLevel | null {
    if (!this.levelCache.has(index)) this.levelCache.set(index, resolveLevel(this.config, index));
    return this.levelCache.get(index) ?? null;
  }

  columns(levelIndex: number): NestedTableColumn[] {
    let cached = this.visibleColumnsCache.get(levelIndex);
    if (!cached) {
      cached = resolveLevelColumns(this.config, levelIndex, this.levelRows[levelIndex] ?? [])
        .filter((column) => !column.hidden);
      this.visibleColumnsCache.set(levelIndex, cached);
    }
    return cached;
  }

  children(row: NestedTableRow, levelIndex: number): NestedTableRow[] {
    if (!this.level(levelIndex + 1)) return [];
    const field = this.level(levelIndex)?.childrenField || 'children';
    const value = row[field];
    return Array.isArray(value) ? (value as NestedTableRow[]) : [];
  }

  isExpandable(row: NestedTableRow, levelIndex: number): boolean {
    return !!this.level(levelIndex + 1)
      && (this.children(row, levelIndex).length > 0 || !!this.levelQuery(levelIndex + 1));
  }

  isExpanded(key: string): boolean {
    return this.expanded.has(key);
  }

  toggle(key: string, row?: NestedTableRow, levelIndex?: number): void {
    if (this.expanded.delete(key)) return;
    this.expanded.add(key);
    if (row && levelIndex !== undefined && !this.children(row, levelIndex).length) {
      this.loadChildren(key, row, levelIndex);
    }
  }

  childState(key: string): 'loading' | 'error' | null {
    return this.childStates.get(key) ?? null;
  }

  private levelQuery(index: number): NestedTableLevelQuery | null {
    const query = this.config.levelQueries?.[index];
    return query && Array.isArray(query.columns) ? query : null;
  }

  private loadChildren(key: string, row: NestedTableRow, levelIndex: number): void {
    const query = this.levelQuery(levelIndex + 1);
    // Against the current server - a table saved on another machine may carry its host.
    const url = rebaseSelfServiceApiUrl(this.config.levelApiUrl);
    if (!query || !url || this.loadedChildren.has(key) || this.childRequests.has(key)) return;

    const path = key.split('.').map(Number);
    const request = resolveNestedLevelQuery(query, [...this.ancestorsAt(path), row]);
    this.childStates.set(key, 'loading');
    this.changeDetector.markForCheck();
    const subscription = this.configService.getConfig(url, {
      apiUrl: url,
      skipDefaultPayload: true,
      customPayload: this.config.canvasFilters?.length ? { ...request, canvasFilters: this.config.canvasFilters } : request,
    }, {}, 'post').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: unknown) => {
        const rows = normalizeNestedQueryRows(nestedQueryRows(response, this.config.rowsKey), request.columns);
        const field = this.level(levelIndex)?.childrenField || 'children';
        row[field] = rows;
        this.loadedChildren.add(key);
        this.childStates.delete(key);
        this.childRequests.delete(key);
        this.levelRows = rowsByLevel(this.config, this.rows);
        this.visibleColumnsCache.delete(levelIndex + 1);
        this.rangeCache.clear();
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.childStates.set(key, 'error');
        this.childRequests.delete(key);
        this.changeDetector.markForCheck();
      },
    });
    // HTTP observables are asynchronous, but tests/mocks may emit synchronously.
    // Do not leave an already-completed request registered in that case.
    if (this.childStates.get(key) === 'loading') this.childRequests.set(key, subscription);
  }

  rowKey(parentKey: string, index: number): string {
    return parentKey ? `${parentKey}.${index}` : String(index);
  }

  /** A header clicked to sort, per level: that column first, ahead of the level's own order. */
  private readonly clickSorts = new Map<number, NestedSortKey>();
  /** Sorted copies by rows array, until the order or the rows change (edits do not re-sort). */
  private readonly sortedCache = new WeakMap<NestedTableRow[], { signature: string; rows: NestedTableRow[] }>();

  /** The order a level's rows are shown in: a clicked header first, then the level's `sortBy`. */
  sortKeys(levelIndex: number): NestedSortKey[] {
    const own = this.level(levelIndex)?.sortBy ?? [];
    const clicked = this.clickSorts.get(levelIndex);
    return clicked ? [clicked, ...own.filter((key) => key.field !== clicked.field)] : own;
  }

  /**
   * A level's rows in display order. The rows themselves stay where they are:
   * a row's key (expanded, edited, saved) is its position in `rows`, see rowKey.
   */
  sortedRows(rows: NestedTableRow[], levelIndex: number): NestedTableRow[] {
    const keys = this.sortKeys(levelIndex);
    if (!keys.length || !rows?.length) return rows;
    const signature = JSON.stringify(keys);
    const cached = this.sortedCache.get(rows);
    if (cached?.signature === signature) return cached.rows;
    const sorted = sortNestedRows(rows, keys, this.columns(levelIndex));
    this.sortedCache.set(rows, { signature, rows: sorted });
    return sorted;
  }

  /** A level whose headers sort when clicked (its `headerSort` setting). */
  headerSortable(levelIndex: number, column: NestedTableColumn): boolean {
    return this.level(levelIndex)?.headerSort === true && !!column.field && column.type !== 'index';
  }

  /** Header click (only where `headerSort` is on): ascending, again descending, a third time back to the level's own order. */
  toggleSort(levelIndex: number, column: NestedTableColumn): void {
    if (!this.headerSortable(levelIndex, column)) return;
    const current = this.clickSorts.get(levelIndex);
    if (current?.field !== column.field) this.clickSorts.set(levelIndex, { field: column.field, direction: 'asc' });
    else if (current.direction === 'asc') this.clickSorts.set(levelIndex, { field: column.field, direction: 'desc' });
    else this.clickSorts.delete(levelIndex);
  }

  /** The arrow on a header: the level is sorted by that column first. */
  sortIndicator(levelIndex: number, column: NestedTableColumn): '' | 'asc' | 'desc' {
    const first = this.sortKeys(levelIndex)[0];
    if (!first || !column.field) return '';
    const wanted = first.field.toLowerCase().replace(/[^a-z0-9]/g, '');
    const matches = [column.field, column.label].some((name) => !!name && name.toLowerCase().replace(/[^a-z0-9]/g, '') === wanted);
    return matches ? first.direction ?? 'asc' : '';
  }

  trackIndex(index: number): number {
    return index;
  }

  // ── Cells ──────────────────────────────────────────────────────────────────

  // ── Paired columns (one column from two database columns) ──────────────────

  /** The row keys a column shows: its field, plus `secondField` when paired. */
  cellFields(column: NestedTableColumn): string[] {
    return column.secondField && column.secondField !== column.field ? [column.field, column.secondField] : [column.field];
  }

  isPaired(column: NestedTableColumn): boolean {
    return this.cellFields(column).length > 1;
  }

  /** What sits between the two values of a paired column ("6% - 10%"). */
  separator(column: NestedTableColumn): string {
    const text = column.separator;
    return typeof text === 'string' && text.trim() ? text.trim() : '-';
  }

  /** The cell's text: a paired column joins both formatted values with its separator. */
  display(column: NestedTableColumn, row: NestedTableRow, index: number): string {
    if (column.type === 'index') return String(index + 1);
    if (this.isPaired(column)) {
      return this.cellFields(column).map((field) => this.formatValue(column, row[field])).join(` ${this.separator(column)} `);
    }
    return this.formatValue(column, row[column.field]);
  }

  /** One value formatted by the column's type / prefix / suffix / decimals. */
  formatValue(column: NestedTableColumn, value: unknown): string {
    if (isBlank(value)) return column.emptyText ?? this.config.emptyText ?? '—';
    const type = column.type ?? 'text';
    const numeric = type === 'number' || type === 'currency' || type === 'percent';
    // "Round off" on a plain column: numeric values are rounded, text is left alone.
    const roundsText = type === 'text' && typeof column.decimals === 'number';
    // "Number scale" (K / M / B) on a plain column too: numbers are scaled, text is left alone.
    const scalesText = type === 'text' && !!column.scale;
    // "Convert currency": value × the picked currency's rate, its symbol in front.
    const ticked = column.convertCurrency ?? this.cellConfig(column)?.convertCurrency;
    const currency = ticked ? cellCurrency({ convertCurrency: true }, this.config.currency) : null;
    const converts = !!currency && Number.isFinite(Number(value));
    let text: string;
    if ((numeric || roundsText || scalesText || converts) && Number.isFinite(Number(value))) {
      const number = Number(value) * (converts ? currency!.rate : 1);
      const { divisor, unit } = numberScaleOf(column.scale, number);
      // Scaled values keep one decimal unless "Round off" says otherwise (23.0M, 7.7M).
      const decimals = column.decimals ?? (unit ? 1 : 0);
      const body = (number / divisor).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      const prefix = converts && currency!.symbol ? currency!.symbol : this.prefix(column);
      text = `${prefix}${body}${unit}${this.suffix(column)}`;
    } else {
      text = `${column.prefix ?? ''}${String(value)}${column.suffix ?? ''}`;
    }
    // The shared config's own prefix / suffix wrap the column's formatting.
    const cfg = column.cellConfig;
    const cfgPrefix = converts && currency!.symbol ? undefined : cfg?.prefix;
    return cfg && (cfgPrefix || cfg.suffix) ? decorateCellText({ prefix: cfgPrefix, suffix: cfg.suffix }, text) : text;
  }

  // ── Generic cell configuration (shared with every other table type) ─────────
  // The column's legacy keys (colour / bold / colorRules / badgeStyles /
  // options / placeholder) and its explicit `cellConfig` are folded into ONE
  // TableCellConfig (nestedColumnCellConfig) and rendered through the shared
  // resolver — the same code path the Normal / Editable / Drill-Down tables use.
  private readonly cellConfigCache = new WeakMap<NestedTableColumn, TableCellConfig | null>();
  private readonly rangeCache = new Map<string, CellValueRange | null>();

  cellConfig(column: NestedTableColumn): TableCellConfig | null {
    let cfg = this.cellConfigCache.get(column);
    if (cfg === undefined) {
      cfg = nestedColumnCellConfig(column) ?? null;
      this.cellConfigCache.set(column, cfg);
    }
    return cfg;
  }
  /** Which cell template renders this column. */
  cellKind(column: NestedTableColumn): NestedCellKind {
    return nestedCellKind(column, this.cellConfig(column) ?? undefined);
  }
  private look(column: NestedTableColumn, row: NestedTableRow): ResolvedCellLook {
    return resolveCellLook(this.cellConfig(column), row[column.field], row);
  }
  /** Numeric range of a column at a level (data bars / heat maps). */
  private range(column: NestedTableColumn, levelIndex: number): CellValueRange | null {
    const key = `${levelIndex}:${column.field}`;
    if (!this.rangeCache.has(key)) {
      let min = Infinity;
      let max = -Infinity;
      (this.levelRows[levelIndex] ?? []).forEach((row) => {
        const n = Number(row[column.field]);
        if (!Number.isFinite(n) || isBlank(row[column.field])) return;
        if (n < min) min = n;
        if (n > max) max = n;
      });
      this.rangeCache.set(key, min === Infinity ? null : { min, max });
    }
    return this.rangeCache.get(key) ?? null;
  }
  cellIcon(column: NestedTableColumn, row: NestedTableRow): string {
    const cfg = this.cellConfig(column);
    if (!cfg) return '';
    const look = this.look(column, row);
    if (look.icon) return look.icon;
    return cfg.display === 'trend' ? cellTrend(row[column.field]).icon : '';
  }
  cellTooltip(column: NestedTableColumn, row: NestedTableRow): string | null {
    const cfg = this.cellConfig(column);
    return cfg?.tooltip ? substituteCellTokens(cfg.tooltip, row[column.field], row) : null;
  }
  cellAccent(column: NestedTableColumn, row: NestedTableRow): string {
    return cellAccent(this.cellConfig(column), this.look(column, row));
  }
  cellPercent(column: NestedTableColumn, row: NestedTableRow, levelIndex: number): number {
    return cellBarPercent(this.cellConfig(column), row[column.field], this.range(column, levelIndex));
  }
  cellHref(column: NestedTableColumn, row: NestedTableRow): string {
    const cfg = this.cellConfig(column);
    const value = row[column.field];
    return cfg?.linkTemplate ? substituteCellTokens(cfg.linkTemplate, value, row) : String(value ?? '');
  }
  /** Inline style of the cell's editor so the configured look survives editing. */
  editorStyle(column: NestedTableColumn, row: NestedTableRow): Record<string, string> {
    const cfg = this.cellConfig(column);
    if (!cfg) return {};
    const style = cellLookStyle(this.look(column, row));
    delete style['background'];
    return style;
  }

  prefix(column: NestedTableColumn): string {
    return column.prefix ?? (column.type === 'currency' ? '$' : '');
  }

  suffix(column: NestedTableColumn): string {
    return column.suffix ?? (column.type === 'percent' ? '%' : '');
  }

  /** Adornments inside an editor box ("6%"): the column's own, else the
   *  shared Cell formatting's prefix / suffix — so a data column switched to
   *  Cell input = Number shows "[ 6% ] - [ 10% ]" like the read-only text. */
  editorPrefix(column: NestedTableColumn): string {
    return this.prefix(column) || this.cellConfig(column)?.prefix || '';
  }

  editorSuffix(column: NestedTableColumn): string {
    return this.suffix(column) || this.cellConfig(column)?.suffix || '';
  }

  cellStyle(column: NestedTableColumn, row: NestedTableRow, levelIndex = 0): Record<string, string> {
    const cfg = this.cellConfig(column);
    if (!cfg) return {};
    const look = this.look(column, row);
    const style = cellLookStyle(look);
    if (cfg.display === 'heatmap' && !look.rule?.background && cellDisplayNeedsRange(cfg)) {
      const heat = cellHeatColor(cfg, row[column.field], this.range(column, levelIndex));
      if (heat) style['background'] = heat;
    }
    if (cfg.display === 'trend' && !look.rule?.color) style['color'] = cellTrend(row[column.field]).color;
    if (look.highlight) style['box-shadow'] = 'inset 3px 0 0 rgba(245, 158, 11, .85)';
    return style;
  }

  headerWidth(levelIndex: number): number | null {
    return textSizePx(this.config[`level${levelIndex + 1}HeaderWidth`])
      ?? textSizePx(this.config.tableHeaderWidth);
  }

  /**
   * "Header width" is each column's width AT MOST: the table is that many px
   * wide, but never wider than the panel it sits in — then the columns share
   * the space equally (fixed layout) instead of scrolling sideways.
   */
  tableWidth(levelIndex: number): string | null {
    const width = this.headerWidth(levelIndex);
    if (width === null) return null;
    const columns = this.columns(levelIndex);
    const total = columns.reduce((sum, column) => sum + (this.ownWidthPx(column) ?? width), 0);
    return `min(100%, ${total}px)`;
  }

  columnWidth(column: NestedTableColumn, levelIndex: number): string | null {
    const own = column.width === undefined || column.width === null || column.width === ''
      ? null
      : typeof column.width === 'number' ? `${column.width}px` : column.width;
    const header = this.headerWidth(levelIndex);
    if (header === null) return own;
    // A column's own Width beats the level's Header width. Once one column has
    // its own width, the others keep the Header width (the table may scroll)
    // instead of sharing what is left equally.
    if (own) return own;
    return this.columns(levelIndex).some((item) => this.ownWidthPx(item) !== null) ? `${header}px` : null;
  }

  /** A column's own width in px (number or "180px"), else null. */
  private ownWidthPx(column: NestedTableColumn): number | null {
    const width = column.width;
    if (typeof width === 'number') return width > 0 ? width : null;
    const match = typeof width === 'string' ? /^\s*(\d+(?:\.\d+)?)\s*(px)?\s*$/.exec(width) : null;
    return match ? Number(match[1]) : null;
  }

  headerLabel(column: NestedTableColumn): string {
    return column.required ? `${column.label} *` : column.label;
  }

  badgeStyle(column: NestedTableColumn, row: NestedTableRow): Record<string, string> {
    const value = String(row[column.field] ?? '');
    const style = { ...(column.badge ?? {}), ...(column.badgeStyles?.[value] ?? {}) };
    const cfg = this.cellConfig(column);
    // Shared-config badge (what the shared editor writes): the matching rule
    // or value colour, else the accent — soft wash or solid fill (badgeFill).
    // The legacy per-column badge colours fill in only when nothing matched.
    if (cfg && (cfg.display === 'badge' || cfg.rules?.length || cfg.valueColors?.length || cfg.color)) {
      const look = this.look(column, row);
      const shared = cellBadgeLookStyle(cfg, look);
      if (!look.rule && !look.valueColor) {
        if (style.color) shared['color'] = style.color;
        if (style.background) shared['background'] = style.background;
      }
      return shared;
    }
    return {
      background: style.background ?? this.theme.headerBackground,
      color: style.color ?? '#ffffff',
    };
  }
  /** Badge text: the matching rule's label, else the formatted value. */
  badgeText(column: NestedTableColumn, row: NestedTableRow, index: number): string {
    const cfg = this.cellConfig(column);
    const label = cfg ? this.look(column, row).label : undefined;
    return label || this.display(column, row, index);
  }

  /** Dropdown choices; `field` is which of a paired column's keys this select edits. */
  options(column: NestedTableColumn, levelIndex = 0, row?: NestedTableRow, field = column.field): NestedOption[] {
    const cfg = this.cellConfig(column);
    const labels = new Map<string, string>();
    (column.options ?? []).forEach((option) => {
      if (typeof option === 'string') labels.set(option, option);
      else labels.set(option.value, option.label);
    });
    const values = cfg
      ? cellDropdownOptions(cfg, cfg.optionsFromData ? (this.levelRows[levelIndex] ?? []).map((row) => row[column.field]) : [])
      : Array.from(labels.keys());
    // The row's saved value is always selectable — otherwise a value that is not
    // in the configured list showed as an empty "Select…".
    const current = row ? row[field] : null;
    const currentText = current === null || current === undefined ? '' : String(current);
    const all = currentText && !values.includes(currentText) ? [currentText, ...values] : values;
    return all.map((value) => ({ value, label: labels.get(value) ?? value }));
  }
  placeholder(column: NestedTableColumn): string {
    return column.placeholder || this.cellConfig(column)?.placeholder || '';
  }

  /** The editor's text for one of the column's keys (its field by default). */
  editorValue(column: NestedTableColumn, row: NestedTableRow, field = column.field): string {
    const value = row[field];
    if (isBlank(value)) return '';
    // "Round off" on an input column: a number shows at that many decimals.
    const rounded = this.roundOff(column, value);
    return rounded === null ? String(value) : rounded.toFixed(column.decimals as number);
  }

  /** The value rounded to the column's "Round off" decimals; null when the
   *  column has none or the value is not a number (text is left alone). */
  private roundOff(column: NestedTableColumn, value: unknown): number | null {
    const decimals = column.decimals;
    if (typeof decimals !== 'number' || decimals < 0 || isBlank(value)) return null;
    const number = typeof value === 'number' ? value : Number(String(value).trim());
    if (!Number.isFinite(number)) return null;
    // Exponent shift, not × 10^d: 1.005 rounds to 1.01, not 1.00. A number
    // already in exponent form (1e21) cannot be shifted that way - leave it.
    const rounded = Number(`${Math.round(Number(`${number}e${decimals}`))}e-${decimals}`);
    return Number.isFinite(rounded) ? rounded : number;
  }

  /** Width of a paired box in `ch` so it hugs its value: "6%" stays one token,
   *  "555%" grows; an empty box keeps room for its placeholder. */
  editorWidthCh(column: NestedTableColumn, row: NestedTableRow, field: string): number {
    const length = this.editorValue(column, row, field).length || (this.placeholder(column) || '—').length;
    return Math.min(14, Math.max(2, length) + 1);
  }

  /** A required editor is invalid while ANY of its keys is blank. */
  isInvalid(column: NestedTableColumn, row: NestedTableRow, field?: string): boolean {
    if (column.required !== true) return false;
    const fields = field ? [field] : this.cellFields(column);
    return fields.some((key) => isBlank(row[key]));
  }

  /** A min / max setting: a plain number, else the row's value in that column (name matched loosely). */
  /**
   * A min / max setting: one or more columns or numbers, comma-separated. Several
   * = the lowest of them for the minimum, the highest for the maximum.
   */
  private boundOf(setting: string | undefined, row: NestedTableRow, pick: 'lowest' | 'highest'): { value: number; from: string } | null {
    const values = String(setting ?? '').split(',')
      .map((part) => ({ value: this.singleBound(part, row), from: this.boundName(part) }))
      .filter((entry): entry is { value: number; from: string } => entry.value !== null);
    if (!values.length) return null;
    return values.reduce((best, entry) => (pick === 'lowest' ? entry.value < best.value : entry.value > best.value) ? entry : best);
  }

  /** A limit's source for the user: the column's header (else its key), or the typed number. */
  private boundName(setting: string): string {
    const text = setting.trim();
    if (/^-?\d+(\.\d+)?$/.test(text)) return text;
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const column = (this.config?.levels ?? []).flatMap((level) => level.columns ?? [])
      .find((candidate) => norm(String(candidate.field ?? '')) === norm(text));
    return column?.label || text;
  }

  private singleBound(setting: string, row: NestedTableRow): number | null {
    const text = setting.trim();
    if (!text) return null;
    if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text);
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const key = Object.keys(row).find((candidate) => norm(candidate) === norm(text));
    const raw = key === undefined ? null : row[key];
    const num = raw === null || raw === undefined || raw === '' ? NaN : Number(String(raw).replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(num) ? num : null;
  }

  /** The editor's change: store it, then show what was stored (rounding). */
  onEditorChange(event: Event, levelIndex: number, key: string, row: NestedTableRow, column: NestedTableColumn, field: string): void {
    const input = event.target as HTMLInputElement;
    this.onEdit(levelIndex, key, row, column, input.value, field);
    input.value = String(this.editorValue(column, row, field) ?? '');
  }

  /**
   * The allowed range: each limit moved by its own signed % (`minOffsetPct` /
   * `maxOffsetPct`, unset = 0) - cost 260,663 at -10 and best_price 514,083
   * at 10 allow 234,597 … 565,491. Null on a side with no limit.
   */
  private allowedRange(column: NestedTableColumn, row: NestedTableRow): { min: number | null; max: number | null; minFrom: string; maxFrom: string } {
    const pctOf = (pct: unknown) => (Number.isFinite(Number(pct)) ? Number(pct) : 0);
    const shift = (bound: { value: number } | null, pct: unknown) =>
      bound === null ? null : bound.value + Math.abs(bound.value) * pctOf(pct) / 100;
    // "Current Price -10%" - which column the limit came from, with its %.
    const from = (bound: { from: string } | null, pct: unknown) =>
      !bound ? '' : pctOf(pct) ? `${bound.from} ${pctOf(pct) > 0 ? '+' : ''}${pctOf(pct)}%` : bound.from;
    const low = this.boundOf(column.minValue, row, column.minPick ?? 'lowest');
    const high = this.boundOf(column.maxValue, row, column.maxPick ?? 'highest');
    return {
      min: shift(low, column.minOffsetPct),
      max: shift(high, column.maxOffsetPct),
      minFrom: from(low, column.minOffsetPct),
      maxFrom: from(high, column.maxOffsetPct),
    };
  }

  /** A typed number outside the allowed range: red border, and the row cannot be saved. */
  isOutOfRange(column: NestedTableColumn, row: NestedTableRow, field?: string): boolean {
    if (!column.minValue && !column.maxValue) return false;
    const { min, max } = this.allowedRange(column, row);
    return (field ? [field] : this.cellFields(column)).some((key) => {
      const value = row[key];
      if (value === null || value === undefined || value === '') return false;
      const num = Number(value);
      return Number.isFinite(num) && ((min !== null && num < min) || (max !== null && num > max));
    });
  }

  /** The message box text: the column's "Limit message" with {min} {max} {value} {label} filled in. */
  rangeMessage(column: NestedTableColumn, row: NestedTableRow, field: string): string {
    const { min, max, minFrom, maxFrom } = this.allowedRange(column, row);
    const fmt = (n: number | null) => (n === null ? '' : Math.round(n).toLocaleString());
    const typed = Number(row[field]);
    return String(column.rangeMessage ?? '').trim()
      .replace(/\{minFrom\}/g, minFrom)
      .replace(/\{maxFrom\}/g, maxFrom)
      .replace(/\{min\}/g, fmt(min))
      .replace(/\{max\}/g, fmt(max))
      .replace(/\{value\}/g, Number.isFinite(typed) ? typed.toLocaleString() : String(row[field] ?? ''))
      .replace(/\{label\}/g, column.label || field);
  }

  /** Hover text on an editor with limits: "Min 405 (Current Price -10%) · Max 660 (Target Price +10%)". */
  boundsHint(column: NestedTableColumn, row: NestedTableRow): string | null {
    const { min, max, minFrom, maxFrom } = this.allowedRange(column, row);
    const fmt = (n: number) => Math.round(n).toLocaleString();
    const parts = [
      min !== null ? `Min ${fmt(min)}${minFrom ? ` (${minFrom})` : ''}` : '',
      max !== null ? `Max ${fmt(max)}${maxFrom ? ` (${maxFrom})` : ''}` : '',
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : null;
  }

  onNumericKeydown(event: KeyboardEvent, column: NestedTableColumn): void {
    if (!(column.numericOnly || column.inputType === 'number')) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (/^\d$/.test(event.key) || ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const input = event.target as HTMLInputElement | null;
    const value = input?.value ?? '';
    const selected = !!input && input.selectionStart !== input.selectionEnd;
    if (event.key === '.' && (!value.includes('.') || selected)) return;
    if (event.key === '-' && ((input?.selectionStart ?? 0) === 0) && (!value.includes('-') || selected)) return;
    event.preventDefault();
  }

  sanitizeNumericInput(event: Event, column: NestedTableColumn): void {
    if (!(column.numericOnly || column.inputType === 'number')) return;
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    let cleaned = input.value.replace(/[^0-9.\-]/g, '');
    const negative = cleaned.startsWith('-');
    cleaned = cleaned.replace(/-/g, '').replace(/\.(?=.*\.)/g, '');
    input.value = `${negative ? '-' : ''}${cleaned}`;
  }

  /** An editor changed; `field` names which of a paired column's keys (its field by default). */
  onEdit(levelIndex: number, key: string, row: NestedTableRow, column: NestedTableColumn, raw: string, field = column.field): void {
    // A read-only editor never changes its row (guards against a forced change event).
    if (column.readOnly) return;
    const editor = this.cellConfig(column)?.editor;
    // A prefix / suffix (either the column's or the shared Cell formatting's)
    // on a non-text editor means a number: "6%" is stored as 6.
    const numeric = column.numericOnly === true || column.inputType === 'number' || editor === 'number'
      || (column.inputType !== 'text' && editor !== 'text' && (!!this.editorPrefix(column) || !!this.editorSuffix(column)));
    const cleaned = numeric ? raw.replace(/[^0-9.\-]/g, '') : raw;
    let value: string | number | null = numeric ? (cleaned === '' ? null : Number(cleaned)) : raw;
    // "Round off": a typed number is stored rounded, so what is saved matches what is shown.
    const rounded = this.roundOff(column, value);
    if (rounded !== null) value = rounded;
    row[field] = value;
    // Edited since the last save: its Save button can show (see canSave). A
    // paired column's second key is tracked (and sent) under its own name.
    const fields = this.dirtyRows.get(key) ?? new Set<string>();
    fields.add(field);
    this.dirtyRows.set(key, fields);
    this.saveStates.delete(key);
    // A new edit after a save: the table's "Saved ✓" gives way to its Save again.
    this.tableSaveStates.delete(this.tableKey(levelIndex, key.includes('.') ? key.slice(0, key.lastIndexOf('.')) : ''));
    this.cellChange.emit({
      levelIndex,
      path: key.split('.').map(Number),
      field,
      value,
      row,
    });
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  /** A cell button: emit it with its row (no child rows) and the rows above it. */
  onCellButtonClick(button: TableCellButton, levelIndex: number, column: NestedTableColumn, row: NestedTableRow, key: string, event: Event): void {
    event.stopPropagation();
    const flat = (item: NestedTableRow) => Object.fromEntries(Object.entries(item).filter(([, value]) => !Array.isArray(value))) as NestedTableRow;
    this.cellButton.emit({
      button,
      levelIndex,
      field: column.field,
      row: flat(row),
      ancestors: this.ancestorsAt(key.split('.').map(Number)).map(flat),
    });
  }

  runAction(actionId: string, levelIndex: number, parentRow: NestedTableRow | null, rows: NestedTableRow[], parentKey = ''): void {
    if (actionId === 'export') this.exportCsv(levelIndex, rows);
    // Every row above this table, top level first (the customer, then the segment, ...).
    const ancestors = parentRow
      ? [...(parentKey ? this.ancestorsAt(parentKey.split('.').map(Number)) : []), parentRow]
      : [];
    this.action.emit({ actionId, levelIndex, parentRow, ancestors, rows });
  }

  private exportCsv(levelIndex: number, rows: NestedTableRow[]): void {
    const columns = this.columns(levelIndex);
    const escape = (text: string) => `"${text.replace(/"/g, '""')}"`;
    const lines = [
      columns.map((column) => escape(column.label)).join(','),
      ...rows.map((row, index) => columns.map((column) => escape(this.display(column, row, index))).join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.level(levelIndex)?.title || this.config.title || 'table'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private initialExpansion(rows: NestedTableRow[], levelIndex: number, parentKey: string, into = new Set<string>()): Set<string> {
    const startExpanded = this.level(levelIndex)?.startExpanded === true;
    rows.forEach((row, index) => {
      const key = this.rowKey(parentKey, index);
      const children = this.children(row, levelIndex);
      if (!children.length) return;
      const flag = row['expanded'];
      if (flag === true || (flag !== false && startExpanded)) into.add(key);
      this.initialExpansion(children, levelIndex + 1, key, into);
    });
    return into;
  }
}

function flatTheme(config: NestedTableData): Partial<NestedTableTheme> {
  const source = config as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of NESTED_THEME_KEYS) {
    const value = source[key];
    if (value === undefined || value === null || value === '') continue;
    // A tile Format sends fontSize 0 for "default" — taken literally it hides every cell.
    if (key === 'fontSize' && !(Number(value) > 0)) continue;
    out[key] = value;
  }
  return out as Partial<NestedTableTheme>;
}

/** ConfigService already unwraps `responseList[0].data[0]`; rows sit under `rowsKey`. */
function extractRows(response: unknown, rowsKey = 'tableRows'): NestedTableRow[] {
  if (Array.isArray(response)) return response as NestedTableRow[];
  const body = (response ?? {}) as Record<string, unknown>;
  const candidate = body[rowsKey] ?? body['rows'] ?? body['data'];
  return Array.isArray(candidate) ? (candidate as NestedTableRow[]) : [];
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function cloneRows(rows: NestedTableRow[]): NestedTableRow[] {
  return typeof structuredClone === 'function'
    ? structuredClone(rows)
    : (JSON.parse(JSON.stringify(rows)) as NestedTableRow[]);
}

/** The divisor and unit for a column's number scale (auto = by the value's own size). */
export function numberScaleOf(scale: string | undefined, value: number): { divisor: number; unit: string } {
  const magnitude = Math.abs(value);
  const pick = scale === 'auto'
    ? (magnitude >= 1e9 ? 'B' : magnitude >= 1e6 ? 'M' : magnitude >= 1e3 ? 'K' : '')
    : scale ?? '';
  switch (pick) {
    case 'K': return { divisor: 1e3, unit: 'K' };
    case 'M': return { divisor: 1e6, unit: 'M' };
    case 'B': return { divisor: 1e9, unit: 'B' };
    default: return { divisor: 1, unit: '' };
  }
}
